import { useCallback, useRef, useState } from 'react';

import { BannerSizes, BannerTypes, usePlaydeckAds } from '@tonplay/playdeck-ads';
import WebApp from '@twa-dev/sdk';

import { useAnalyticsService } from '@/app/providers/analytics-service/hooks';

import {
  type Advert,
  type AdvertSizes,
  AdvertTypes,
  clickAdvert,
  getAdverts,
  showAdvert,
} from '@/shared/api/adverts';
import { openTelegramLink } from '@/shared/helpers/tg-links/open-tg-link';
import { pdAdBannerShown } from '@/shared/services/analytics/events/pd-ad-banner-shown';

import { CustomErrorCode } from './types';

const BANNER_TYPE_BY_ADVERT_TYPE: Record<AdvertTypes, BannerTypes> = {
  [AdvertTypes.BANNER]: BannerTypes.BANNER,
  [AdvertTypes.BLOCK]: BannerTypes.BLOCK,
};

const BANNER_SIZE_BY_ADVERT_TYPE: Record<AdvertSizes, BannerSizes> = {
  [BannerSizes.LARGE]: BannerSizes.LARGE,
  [BannerSizes.MEDIUM]: BannerSizes.MEDIUM,
  [BannerSizes.SMALL]: BannerSizes.SMALL,
};

const BANNERS_COUNT = 3;

export class ErrorWithCode extends Error {
  public code?: string;
  constructor(message: string, code?: string) {
    super(message);
    if (code) this.code = code;
  }
}

export interface PlaydeckAdsProps {
  onReward?: () => void;
  onError?: (error: ErrorWithCode) => void;

  taskId?: number;
}

export const usePlaydeckAdvertisement = (
  source: string,
  {
    onReward,
    onError,

    taskId,
  }: PlaydeckAdsProps = {}
) => {
  const [adverts, setAdverts] = useState<Advert[]>([]);

  const isCompletedRef = useRef(false);
  const isShowedBannersDictRef = useRef<Record<number, boolean>>({});

  const analyticsService = useAnalyticsService();

  const slides = adverts.map((advert) => ({
    id: advert.id,
    imageUrl: advert.image,
    type: BANNER_TYPE_BY_ADVERT_TYPE[advert.type],
    size: BANNER_SIZE_BY_ADVERT_TYPE[advert.size],
    title: advert.title,
    description: advert.description,
    link: advert.link,
    advertiser: advert.advertiser,
  }));

  const sendAlOnCompleteBanner = ({ advertiser, imageUrl }: (typeof slides)[number]) => {
    const e = pdAdBannerShown({
      event_properties: {
        advertiser,
        image: imageUrl,
        event_source: source,
        id: taskId,
      },
    });

    analyticsService.send(e);
  };

  const prevHeaderColor = useRef(WebApp.headerColor);
  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  const handleStart = useCallback(() => {
    // store prev isVisible of BackButton to display it correctly after ads
    backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;
    prevHeaderColor.current = WebApp.headerColor;

    if (WebApp.isFullscreen) {
      WebApp.setHeaderColor('#000000');
    }

    if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
  }, []);

  const handleEnd = useCallback(() => {
    WebApp.setHeaderColor(prevHeaderColor.current);

    if (backButtonWasVisibleRef.current) {
      WebApp.BackButton.show();
      backButtonWasVisibleRef.current = true;
    }
  }, []);

  const {
    open: openPlaydeckAds,
    close: closePlaydeckAds,
    ...res
  } = usePlaydeckAds({
    slides,
    onClose: () => {
      isCompletedRef.current = false;
      isShowedBannersDictRef.current = {};

      handleEnd();
    },
    onClickBanner: (advert) => {
      void Promise.all([
        clickAdvert(advert.id),
        // Call showAdvert only if the advert was not shown before
        isShowedBannersDictRef.current[advert.id] ? undefined : showAdvert(advert.id),
      ]).finally(() => {
        if (!isShowedBannersDictRef.current[advert.id]) {
          isShowedBannersDictRef.current[advert.id] = true;
        }

        if (!isCompletedRef.current) {
          // If user clicks any banner call onComplete because opening of links should close webapp
          onReward?.();
        }

        openTelegramLink(advert.link);

        closePlaydeckAds();

        WebApp?.disableClosingConfirmation();
        WebApp?.close();
      });
    },
    onCompleteBanner: (advert, index) => {
      const showAdvertPromise = isShowedBannersDictRef.current[advert.id]
        ? () => Promise.resolve()
        : showAdvert;

      isShowedBannersDictRef.current[advert.id] = true;

      void showAdvertPromise(advert.id).finally(() => {
        sendAlOnCompleteBanner(advert);

        // Call onComplete inside onCompleteBanner to avoid calling onComplete before onCompleteBanner, because showAdvert is promise
        if (index === adverts.length - 1) {
          isCompletedRef.current = true;
          // If user watcher all banners call onComplete
          onReward?.();
        }
      });
    },
    onComplete: () => {},
  });

  const handleErrorGetAdverts = useCallback(
    (err: ErrorWithCode) => {
      handleEnd();

      console.error(err);
      onError?.(err);
    },
    [handleEnd, onError]
  );

  const open = useCallback(() => {
    getAdverts({ limit: BANNERS_COUNT })
      .then((res) => {
        if (!res.length) {
          handleErrorGetAdverts(
            new ErrorWithCode('No advertisements found', CustomErrorCode.NO_AVAILABLE_ADS)
          );
          return;
        }

        setAdverts(res);

        handleStart();

        setTimeout(() => openPlaydeckAds(), 0);
      })
      .catch((error) => {
        handleErrorGetAdverts(error);
      });
  }, [handleErrorGetAdverts, handleStart, openPlaydeckAds]);

  return {
    ...res,
    open,
  };
};
